import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Eyebrow, FadeUp, MaskLine } from "./Reveal";
import { Instagram, Mail } from "lucide-react";
import { useLang, INSTAGRAM_URL } from "@/lib/i18n";

const empty = { name: "", email: "", phone: "", type: "course", message: "" };
const TYPES = ["course", "merch", "killhouse", "general"];

export const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/inquiries", { ...form, subject: "Contact form" });
      toast.success(t("contact.ok"));
      setForm(empty);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="px-5 sm:px-8 lg:px-12 py-24 sm:py-36 grid lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <Eyebrow>{t("contact.eyebrow")}</Eyebrow>
        <h2 className="font-display font-black uppercase text-5xl sm:text-7xl leading-[0.85] mt-5">
          <MaskLine>{t("contact.title")[0]}</MaskLine>
          <MaskLine delay={0.1}>{t("contact.title")[1]}</MaskLine>
        </h2>
        <FadeUp className="mt-8 space-y-4 text-sm text-[var(--muted)]">
          <p>{t("contact.intro")}</p>
          <a href="mailto:developmentgrouparrow@gmail.com" data-testid="contact-email-link"
            className="group flex items-center gap-3 font-mono text-xs tracking-[0.12em] text-[var(--bone)] hover:text-white break-all">
            <Mail className="w-5 h-5 shrink-0" /> <span className="border-b border-transparent group-hover:border-white transition-colors">developmentgrouparrow@gmail.com</span>
          </a>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link"
            className="group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[var(--bone)] hover:text-white">
            <Instagram className="w-5 h-5" /> <span className="border-b border-transparent group-hover:border-white transition-colors">@arrow.development.group</span>
          </a>
        </FadeUp>
      </div>
      <FadeUp className="lg:col-span-7">
        <form data-testid="contact-form" onSubmit={submit} className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {TYPES.map((v, i) => (
              <button type="button" key={v} data-testid={`contact-type-${v}`} onClick={() => setForm({ ...form, type: v })}
                className={`font-mono text-xs uppercase tracking-[0.2em] px-4 py-2.5 border transition-colors ${form.type === v ? "bg-[var(--signal)] border-[var(--signal)] text-[var(--ink)]" : "border-[var(--line)] hover:border-[var(--bone)]"}`}>
                {t("contact.types")[i]}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <label className="block"><span className="field-label">{t("f.name")}</span>
              <input data-testid="contact-name-input" required className="field" value={form.name} onChange={set("name")} /></label>
            <label className="block"><span className="field-label">{t("f.email")}</span>
              <input data-testid="contact-email-input" required type="email" className="field" value={form.email} onChange={set("email")} /></label>
          </div>
          <label className="block"><span className="field-label">{t("f.message")}</span>
            <textarea data-testid="contact-message-input" required rows={4} className="field resize-none" value={form.message} onChange={set("message")} /></label>
          <button data-testid="contact-submit-button" disabled={busy}
            className="clip-cut-sm bg-[var(--bone)] text-[var(--ink)] font-mono text-xs uppercase tracking-[0.25em] px-10 py-4 hover:bg-[var(--signal)] transition-colors disabled:opacity-60">
            {busy ? t("contact.sending") : t("contact.send")}
          </button>
        </form>
      </FadeUp>
    </section>
  );
};
